import type { BackupEnvelope } from "@app/core";
import { create } from "zustand";
import { gatherSnapshotSync, restoreSnapshot } from "../lib/snapshot.js";
import { tr } from "../lib/tr.js";
import { toast } from "./toast.store.js";

/** One-step undo (C8). Before a user-initiated task/series mutation we snapshot
 *  the whole task/rule/override/label state into a `BackupEnvelope`; `undo()`
 *  restores it wholesale (reusing the backup restore machinery). Single step —
 *  capturing again overwrites the previous snapshot. No redo. */
interface UndoState {
  snapshot: BackupEnvelope | null;
  /** Internal label of the captured action, for debugging only — it is NEVER
   *  rendered, so it stays untranslated English. The user-facing text is the
   *  separate `doneMsg` passed to {@link undoable}, which MUST come from `tr()`. */
  label: string | null;
  busy: boolean;
  capture: (label: string) => void;
  undo: () => Promise<void>;
  clear: () => void;
}

export const useUndoStore = create<UndoState>((set, get) => ({
  snapshot: null,
  label: null,
  busy: false,

  capture(label) {
    // Synchronous in-memory snapshot — no repo reads on the edit critical path.
    set({ snapshot: gatherSnapshotSync(), label });
  },

  async undo() {
    const { snapshot, busy } = get();
    if (!snapshot || busy) return;
    set({ busy: true });
    try {
      await restoreSnapshot(snapshot);
      set({ snapshot: null, label: null });
      toast.success(tr("toast.undo.done"));
    } catch (e) {
      toast.error(tr("toast.undo.failed", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      set({ busy: false });
    }
  },

  clear() {
    set({ snapshot: null, label: null });
  },
}));

/** Toast `action` payload that runs the last undo. Pass to `toast.withAction`. */
export function undoAction(): { label: string; onClick: () => void } {
  return { label: "Undo", onClick: () => void useUndoStore.getState().undo() };
}

// One-step undo coalesces a whole user GESTURE (which may issue several store
// mutations — e.g. a form save that creates a task plus subtasks) into a single
// snapshot + a single Undo toast. A depth counter detects the outermost scope:
// only it snapshots the pre-gesture state and toasts; nested calls just run.
let depth = 0;

/** Enter an undoable scope. Returns true when this is the OUTERMOST scope (the
 *  caller owns the snapshot + the Undo toast). Always pair with `endUndoable()`
 *  in a `finally`. The snapshot is captured only at the outermost entry, so it
 *  reflects the state before the whole gesture. */
export function beginUndoable(label: string): boolean {
  const outer = depth === 0;
  depth++;
  if (outer) useUndoStore.getState().capture(label);
  return outer;
}

export function endUndoable(): void {
  depth = Math.max(0, depth - 1);
}

/** Wrap a mutating action: snapshot (if outermost) → run → Undo toast (if
 *  outermost). Place cheap no-op/early-return guards BEFORE calling this so a
 *  no-op gesture neither snapshots nor toasts.
 *  `label` is internal (debug only); `doneMsg` is shown to the user and must be
 *  a translated string — pass `tr("toast.task.…")`, never a bare literal. */
export async function undoable<T>(label: string, doneMsg: string, fn: () => Promise<T>): Promise<T> {
  const outer = beginUndoable(label);
  try {
    const result = await fn();
    if (outer) toast.withAction("info", doneMsg, undoAction());
    return result;
  } finally {
    endUndoable();
  }
}
